// Single source of truth for the Whop checkout URL + metadata passthrough.
// We embed the Supabase user_id as Whop checkout metadata so the webhook can match the
// membership to the right account by ID even when the user pays with a different email
// than the one on their TraderYo account. Email is still passed (pre-fills the form +
// fallback match). Brackets are kept literal — Whop expects metadata[key]=value.
export const WHOP_MONTHLY_PLAN = 'plan_VZiIXyiKbr7Pr';
export const WHOP_CHECKOUT_URL = `https://whop.com/checkout/${WHOP_MONTHLY_PLAN}`;

// Annual plan. Create it in Whop, then paste its plan id here to switch on the
// monthly/annual toggle in the upgrade UI. Empty = annual hidden (no broken link).
export const WHOP_ANNUAL_PLAN = '';
export const ANNUAL_PRICE_USD = 199; // ~24% off 12×$21.99 — tune to the Whop plan.
export const MONTHLY_PRICE_USD = 21.99;
export const annualAvailable = WHOP_ANNUAL_PLAN.length > 0;

// Founder / lifetime one-time plan. Create a one-time Whop plan, paste its id.
// Empty = the founder deal is hidden. FOUNDER_SEATS_LEFT > 0 shows scarcity copy
// (owner-maintained; set to 0 to hide the counter). One-time purchases still
// create an active Whop membership → existing webhook flips status='active'.
export const WHOP_LIFETIME_PLAN = '';
export const LIFETIME_PRICE_USD = 149;
export const FOUNDER_SEATS_LEFT = 0;
export const lifetimeAvailable = WHOP_LIFETIME_PLAN.length > 0;

export type BillingCycle = 'monthly' | 'annual' | 'lifetime';

export function buildCheckoutUrl(opts: {
  email?: string | null;
  userId?: string | null;
  cycle?: BillingCycle;
}): string {
  const plan =
    opts.cycle === 'lifetime' && lifetimeAvailable
      ? WHOP_LIFETIME_PLAN
      : opts.cycle === 'annual' && annualAvailable
        ? WHOP_ANNUAL_PLAN
        : WHOP_MONTHLY_PLAN;
  const base = `https://whop.com/checkout/${plan}`;
  const parts: string[] = [];
  if (opts.email) parts.push(`email=${encodeURIComponent(opts.email)}`);
  if (opts.userId) parts.push(`metadata[user_id]=${encodeURIComponent(opts.userId)}`);
  return parts.length ? `${base}?${parts.join('&')}` : base;
}

// Safety net for a missed webhook / email mismatch: ask the server to look up the
// caller's Whop membership directly and activate. `email` lets the user supply the
// Whop checkout email if it differs from their account email.
export async function reconcileSubscription(
  email?: string,
): Promise<{ activated: boolean; reason?: string }> {
  const { supabase } = await import('./supabase');
  if (!supabase) return { activated: false, reason: 'demo' };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { activated: false, reason: 'no_session' };
  const res = await fetch('/api/daily-digest?task=reconcile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(email ? { email } : {}),
  });
  const data = await res.json().catch(() => ({}));
  return { activated: !!data.activated, reason: data.reason };
}
